import { db } from '../../firebase';
import { doc, getDoc, setDoc, updateDoc, increment, arrayUnion } from 'firebase/firestore';

/**
 * Channel Context Service
 * Manages channel-level learning and context aggregation
 */

/**
 * Initialize or get channel context
 * @param {string} channelId - Channel ID
 * @returns {Object} Channel context data
 */
export async function getChannelContext(channelId) {
  try {
    const contextRef = doc(db, 'channelContext', channelId);
    const contextDoc = await getDoc(contextRef);
    
    if (contextDoc.exists()) {
      console.log(`­ƒôè [ChannelContext] Retrieved context for channel: ${channelId}`);
      return { id: contextDoc.id, ...contextDoc.data() };
    }
    
    // Initialize empty context
    const initialContext = {
      channelId,
      audienceProfile: '',
      recurringIntents: {},
      historicalFriction: {},
      monetizationSignals: {},
      learnedPatterns: [],
      lastUpdated: new Date(),
      createdAt: new Date()
    };
    
    await setDoc(contextRef, initialContext);
    console.log(`Ô£à [ChannelContext] Initialized context for channel: ${channelId}`);
    return initialContext;
  } catch (error) {
    console.error('ÔØî [ChannelContext] Error getting context:', error);
    throw error;
  }
}

/**
 * Update channel context with new data
 * @param {string} channelId - Channel ID
 * @param {Object} updates - Context updates
 */
export async function updateChannelContext(channelId, updates) {
  try {
    const contextRef = doc(db, 'channelContext', channelId);
    
    await updateDoc(contextRef, {
      ...updates,
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [ChannelContext] Updated context for channel: ${channelId}`);
  } catch (error) {
    console.error('ÔØî [ChannelContext] Error updating context:', error);
    throw error;
  }
}

/**
 * Increment recurring intent count for a channel
 * @param {string} channelId - Channel ID
 * @param {string} intent - Intent type
 */
export async function incrementChannelIntent(channelId, intent) {
  try {
    const contextRef = doc(db, 'channelContext', channelId);
    
    await updateDoc(contextRef, {
      [`recurringIntents.${intent}`]: increment(1),
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [ChannelContext] Incremented intent '${intent}' for channel: ${channelId}`);
  } catch (error) {
    console.error('ÔØî [ChannelContext] Error incrementing intent:', error);
    throw error;
  }
}

/**
 * Increment historical friction theme for a channel
 * @param {string} channelId - Channel ID
 * @param {string} theme - Friction theme
 */
export async function incrementChannelFriction(channelId, theme) {
  try {
    const contextRef = doc(db, 'channelContext', channelId);
    
    await updateDoc(contextRef, {
      [`historicalFriction.${theme}`]: increment(1),
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [ChannelContext] Incremented friction '${theme}' for channel: ${channelId}`);
  } catch (error) {
    console.error('ÔØî [ChannelContext] Error incrementing friction:', error);
    throw error;
  }
}

/**
 * Increment monetization signal for a channel
 * @param {string} channelId - Channel ID
 * @param {string} signal - Monetization signal
 */
export async function incrementMonetizationSignal(channelId, signal) {
  try {
    const contextRef = doc(db, 'channelContext', channelId);
    
    await updateDoc(contextRef, {
      [`monetizationSignals.${signal}`]: increment(1),
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [ChannelContext] Incremented monetization signal '${signal}' for channel: ${channelId}`);
  } catch (error) {
    console.error('ÔØî [ChannelContext] Error incrementing signal:', error);
    throw error;
  }
}

/**
 * Add a learned pattern to the channel context
 * @param {string} channelId - Channel ID
 * @param {string} pattern - Pattern description
 */
export async function addLearnedPattern(channelId, pattern) {
  try {
    const contextRef = doc(db, 'channelContext', channelId);
    
    await updateDoc(contextRef, {
      learnedPatterns: arrayUnion(pattern),
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [ChannelContext] Added learned pattern for channel: ${channelId}`);
  } catch (error) {
    console.error('ÔØî [ChannelContext] Error adding pattern:', error);
    throw error;
  }
}

/**
 * Update audience profile for a channel
 * @param {string} channelId - Channel ID
 * @param {string} profile - Audience profile description
 */
export async function updateAudienceProfile(channelId, profile) {
  try {
    const contextRef = doc(db, 'channelContext', channelId);
    
    await updateDoc(contextRef, {
      audienceProfile: profile,
      lastUpdated: new Date()
    });
    
    console.log(`Ô£à [ChannelContext] Updated audience profile for channel: ${channelId}`);
  } catch (error) {
    console.error('ÔØî [ChannelContext] Error updating profile:', error);
    throw error;
  }
}

/**
 * Get top N recurring intents for a channel
 * @param {Object} channelContext - Channel context data
 * @param {number} topN - Number of top intents to return
 * @returns {Array} Array of { intent, count } objects
 */
export function getTopChannelIntents(channelContext, topN = 5) {
  if (!channelContext?.recurringIntents) return [];
  
  return Object.entries(channelContext.recurringIntents)
    .map(([intent, count]) => ({ intent, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

/**
 * Get top N friction themes for a channel
 * @param {Object} channelContext - Channel context data
 * @param {number} topN - Number of top themes to return
 * @returns {Array} Array of { theme, count } objects
 */
export function getTopChannelFriction(channelContext, topN = 5) {
  if (!channelContext?.historicalFriction) return [];
  
  return Object.entries(channelContext.historicalFriction)
    .map(([theme, count]) => ({ theme, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, topN);
}

